/**
 * useScrollSync.ts
 * Reusable hook for synchronizing horizontal and vertical scroll positions
 * across multiple ScrollView/FlatList refs in a split-pane table layout.
 */

import { useCallback, useRef } from 'react';
import { FlatList, NativeScrollEvent, NativeSyntheticEvent, ScrollView } from 'react-native';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ScrollSyncRefs {
	headerScrollRef: React.RefObject<ScrollView>;
	bodyScrollRef: React.RefObject<ScrollView>;
	grandTotalScrollRef: React.RefObject<ScrollView>;
	stickyListRef: React.RefObject<FlatList>;
	bodyListRef: React.RefObject<FlatList>;
}

export interface ScrollSyncHandlers {
	onHeaderHorizontalScroll: (e: NativeSyntheticEvent<NativeScrollEvent>) => void;
	onBodyHorizontalScroll: (e: NativeSyntheticEvent<NativeScrollEvent>) => void;
	onStickyVerticalScroll: (e: NativeSyntheticEvent<NativeScrollEvent>) => void;
	onBodyVerticalScroll: (e: NativeSyntheticEvent<NativeScrollEvent>) => void;
}

export interface UseScrollSyncReturn {
	refs: ScrollSyncRefs;
	handlers: ScrollSyncHandlers;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Synchronizes scroll positions between header, body, grand total (horizontal)
 * and sticky column / body FlatLists (vertical).
 *
 * Prevents infinite scroll loops via guard refs.
 */
export const useScrollSync = (): UseScrollSyncReturn => {
	const headerScrollRef = useRef<ScrollView>(null);
	const bodyScrollRef = useRef<ScrollView>(null);
	const grandTotalScrollRef = useRef<ScrollView>(null);
	const stickyListRef = useRef<FlatList>(null);
	const bodyListRef = useRef<FlatList>(null);

	const isHeaderScrolling = useRef(false);
	const isBodyScrolling = useRef(false);

	const onHeaderHorizontalScroll = useCallback(
		(e: NativeSyntheticEvent<NativeScrollEvent>) => {
			if (isBodyScrolling.current) return;
			isHeaderScrolling.current = true;
			const x = e.nativeEvent.contentOffset.x;
			bodyScrollRef.current?.scrollTo({ x, animated: false });
			grandTotalScrollRef.current?.scrollTo({ x, animated: false });
			isHeaderScrolling.current = false;
		},
		[],
	);

	const onBodyHorizontalScroll = useCallback(
		(e: NativeSyntheticEvent<NativeScrollEvent>) => {
			if (isHeaderScrolling.current) return;
			isBodyScrolling.current = true;
			const x = e.nativeEvent.contentOffset.x;
			headerScrollRef.current?.scrollTo({ x, animated: false });
			grandTotalScrollRef.current?.scrollTo({ x, animated: false });
			isBodyScrolling.current = false;
		},
		[],
	);

	const onStickyVerticalScroll = useCallback(
		(e: NativeSyntheticEvent<NativeScrollEvent>) => {
			const y = e.nativeEvent.contentOffset.y;
			bodyListRef.current?.scrollToOffset({ offset: y, animated: false });
		},
		[],
	);

	const onBodyVerticalScroll = useCallback(
		(e: NativeSyntheticEvent<NativeScrollEvent>) => {
			const y = e.nativeEvent.contentOffset.y;
			stickyListRef.current?.scrollToOffset({ offset: y, animated: false });
		},
		[],
	);

	return {
		refs: {
			headerScrollRef,
			bodyScrollRef,
			grandTotalScrollRef,
			stickyListRef,
			bodyListRef,
		},
		handlers: {
			onHeaderHorizontalScroll,
			onBodyHorizontalScroll,
			onStickyVerticalScroll,
			onBodyVerticalScroll,
		},
	};
};
